import { DateTime, Duration } from 'luxon';

import { FormatTenantDisplayDateTime } from '@core/utils/dateHelpers';
import { hasValue } from '@core/utils/equality';

export const REMINDER_MINUTES_STEP = 10;

export const DEFAULT_NOTIFY_BEFORE = 'P1D';

export const REMINDER_BUSINESS_DATE_HOUR = 8;

export const BUSINESS_DATE_MISSING_ERROR =
  'The selected record has no value for this business date. Choose another business date or record.';

export const nextSelectableMinute = (now: DateTime): DateTime =>
  now.startOf('minute').plus({
    minutes: (REMINDER_MINUTES_STEP - (now.minute % REMINDER_MINUTES_STEP)) % REMINDER_MINUTES_STEP,
  });

export const reminderTriggerDate = (
  businessDate: DateTime | null,
  notifyBefore: Duration | string,
  isDateOnly = true,
): DateTime | null => {
  if (!hasValue(businessDate) || !businessDate.isValid) {
    return null;
  }
  const offset = typeof notifyBefore === 'string' ? Duration.fromISO(notifyBefore) : notifyBefore;
  if (!offset.isValid) {
    return null;
  }
  // Date-only business dates carry no time; the server fires them at 08:00.
  const triggerBase = isDateOnly
    ? businessDate.set({ hour: REMINDER_BUSINESS_DATE_HOUR })
    : businessDate;
  return triggerBase.minus(offset);
};

export type ReminderOffsetUnit = 'minutes' | 'hours' | 'days';

export const REMINDER_MAX_OFFSET_MINUTES = 120;

export const REMINDER_MIN_OFFSET_AMOUNT = 1;

export const REMINDER_OFFSET_UNIT_OPTIONS: { id: ReminderOffsetUnit; name: string }[] = [
  { id: 'minutes', name: 'Minute' },
  { id: 'hours', name: 'Hour' },
  { id: 'days', name: 'Day' },
];

export const REMINDER_MINUTE_OPTIONS: string[] = Array.from(
  { length: REMINDER_MAX_OFFSET_MINUTES / REMINDER_MINUTES_STEP },
  (_, index) => String((index + 1) * REMINDER_MINUTES_STEP),
);

export const reminderOffsetUnit = (isoDuration: string): ReminderOffsetUnit => {
  const duration = Duration.fromISO(isoDuration);
  if (!duration.isValid) {
    return 'days';
  }
  const minutes = Math.round(duration.as('minutes'));
  if (minutes > 0 && minutes % (60 * 24) === 0) {
    return 'days';
  }
  if (minutes > 0 && minutes % 60 === 0) {
    return 'hours';
  }
  return 'minutes';
};

export const reminderOffsetAmountForUnit = (unit: ReminderOffsetUnit, amount: number): number =>
  unit === 'minutes'
    ? Math.min(
        Math.max(
          Math.round(amount / REMINDER_MINUTES_STEP) * REMINDER_MINUTES_STEP,
          REMINDER_MINUTES_STEP,
        ),
        REMINDER_MAX_OFFSET_MINUTES,
      )
    : amount;

const TRIGGER_DATE_FORMAT = {
  ...DateTime.DATETIME_SHORT,
  day: '2-digit',
  month: '2-digit',
} as const;

export type ReminderTriggerHint = { isPast: boolean; message: string };

export const reminderTriggerHint = (
  triggerDate: DateTime,
  tenantTimezone: string | null | undefined,
  deviceTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
): ReminderTriggerHint => {
  const { offset } = DateTime.now().setZone(tenantTimezone ?? '');
  const timezoneMessage =
    hasValue(tenantTimezone) && deviceTimezone !== tenantTimezone
      ? ` (in tenant timezone: ${tenantTimezone}, UTC${offset / 60})`
      : '';
  const triggerDateText = FormatTenantDisplayDateTime(triggerDate, TRIGGER_DATE_FORMAT);
  const isPast = triggerDate <= DateTime.now();

  return {
    isPast,
    message: isPast
      ? `This reminder would have been sent on ${triggerDateText}${timezoneMessage}`
      : `You'll be reminded on ${triggerDateText}${timezoneMessage}`,
  };
};
